import { select, Store } from '@ngrx/store';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Parcel } from '../_models';
import { environment } from '../../../environments/environment';
import { MapService } from './map.service';
import { AppState } from '../_reducers';
import { selectAllParcels } from '../_selectors/parcel.selectors';
import { StaticMapProxyService } from './static-map-proxy.service';

@Injectable()
/**
 * Service for managing parcels.
 */
export class ParcelService {
  // Services
  private store = inject(Store<AppState>);
  private http = inject(HttpClient);
  private mapService = inject(MapService);

  private apiBaseUrl = environment.apiBaseUrl;
  private parcelList$: Observable<Parcel[]> = null;
  public parcelsList$: BehaviorSubject<Parcel[]> = new BehaviorSubject<Parcel[]>(null);

  //Services
  private staticMapsProxyService = inject(StaticMapProxyService);

  constructor() {
    this.parcelList$ = this.store.pipe(select(selectAllParcels));
  }

  /**
   * Fetches the list of all parcels from the local store.
   *
   * @returns {Observable<Parcel[]>} - Returns an Observable of Parcels List.
   */
  getFromLocalStore(): Observable<Parcel[]> {
    return this.parcelList$;
  }

  /**
   * Fetches the list of all parcels.
   * Called by the Store Actions and therefore it updates the state of "parcels" in the store.
   *
   * @param {number} year - The year for which the crops inside the Parcel are returned.
   * @returns {Observable<Parcel[]>} - Returns an Observable of Parcels List.
   */
  getParcels(year?: number): Observable<Parcel[]> {
    // we clean the Behaviour subject before valorizing it again
    this.parcelsList$.next(null);
    // Conditionally add the `year` query parameter if it exists
    const url = this.apiBaseUrl + 'api/private/v1/parcels/' + (year ? '?year=' + year : '');

    return this.http.get<Parcel[]>(url).pipe(
      map((response) => {
        if (response) {
          this.parcelsList$.next(this.addStaticMapToParcels(response));
          return response;
        }
      })
    );
  }

  /**
   * Gets the list of parcels for a specific year from the local store if available,
   * otherwise fetches the parcels from the API and updates the local store.
   *
   * @param {number} year - The year for which the parcels are requested.
   * @returns {Promise<Parcel[]>} - Returns a Promise that resolves to the list of parcels.
   */
  async getOrFetchParcels(year: number): Promise<Parcel[]> {
    // Check if the parcels are already available in the BehaviorSubject
    const existingParcels = this.parcelsList$.getValue();
    if (existingParcels) {
      // If the parcels are already available, return them
      return existingParcels;
    }

    // If the parcels are not available, fetch them from the API
    // which will also update the BehaviorSubject with the fetched parcels
    const parcels = await this.getParcels(year).toPromise();

    // Return the fetched parcels
    return parcels;
  }

  /**
   * Get the list of parcels for a specific cropYear.
   * Despite the parcels are not year related, the crops that are returned in the Parcel object are indeed cropYear related.
   *
   * @param {number} year - The cropYear for which we want the list of Parcels.
   * @returns {Observable<Parcel[]>} - Returns an Observable of Parcels List.
   * NB this method does NOT update the store.
   */
  getParcelByYear(year: number): Observable<Parcel[]> {
    return this.http.get<Parcel[]>(this.apiBaseUrl + 'api/private/v1/parcels?year=' + year);
  }

  /**
   * Gets a specific parcel object passing the parcelId.
   * It relies on the parcels stored in the Local Store, therefore no API call.
   *
   * @param {number} id - The ID of the parcel for which we want to retrieve the whole object.
   * @returns {Parcel} - Returns the Parcel object.
   */
  getParcelById = (id: number): Parcel => {
    let item: Parcel;
    this.parcelList$
      .pipe(first((list: Parcel[]) => list && list.length > 0))
      .subscribe((list: Parcel[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  /**
   * Gets a specific parcel object passing the parcelId.
   * It makes an API call.
   *
   * @param {number} id - The ID of the parcel for which we want to retrieve the whole object.
   * @returns {Observable<Parcel>} - Returns an Observable of the specific Parcel.
   */
  getParcel(id: number): Observable<Parcel> {
    return this.http
      .get<Parcel>(this.apiBaseUrl + `api/private/v1/parcels/${id}`)
      .pipe(map((response) => response));
  }

  /**
   * Save the parcel and also the Crop that is linked to it, if any.
   *
   * @param {Parcel} parcelItem - The whole Parcel object that we want to save.
   * NB: the 'field' parameter here must be a number.
   * @returns {Observable<Parcel>} - Returns an Observable of the specific Parcel.
   */
  saveParcel(parcelItem): Observable<Parcel> {
    const parcel = { ...parcelItem };
    delete parcel.lastUpdated;
    delete parcel.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    parcel.companyId = companySelected ? companySelected.id : null;

    if (parcel.id) {
      return this.http
        .put<Parcel>(this.apiBaseUrl + `api/private/v1/parcels/${parcel.id}`, parcel)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Parcel>(this.apiBaseUrl + 'api/private/v1/parcels', parcel)
        .pipe(map((response) => response));
    }
  }

  /**
   * Deletes a specific Parcel.
   * NB: the Parcel must not have any crops linked to it. This checks should be triggered anyway.
   *
   * @param {id} number - The whole Parcel object that we want to delete.
   * @returns {} - Returns empty object.
   */
  deleteParcel(id: number): Observable<Parcel> {
    return this.http.delete<Parcel>(this.apiBaseUrl + `api/private/v1/parcels/${id}`).pipe(
      map((response) => {
        return response;
      })
    );
  }

  //! Not used so far
  bulkUpdateParcels(parcels: Parcel[]): Observable<Parcel[]> {
    return this.http
      .put<Parcel[]>(this.apiBaseUrl + 'api/private/v1/parcels/bulkUpdate', parcels)
      .pipe(map((response) => response));
  }

  /**
   * This method is used to link a crop to a specific parcel.
   *
   * @param {number} cropId - The ID of the crop that needs to be linked to the parcel.
   * @param {number} parcelId - The ID of the parcel to which the crop will be linked.
   * @returns {Observable<any>} - Returns an Observable that emits the server response after attempting to link the crop to the parcel.
   */
  linkCropToParcel(cropId: number, parcelId: number): Observable<any> {
    const body = [{ crop: cropId, parcel: parcelId }];
    return this.http
      .put<any>(this.apiBaseUrl + 'api/private/v1/crops/linkParcel', body)
      .pipe(map((response) => response));
  }

  /**
   * Adds static map to parcels.
   *
   * @param {Parcel[]} parcels - The list of parcels to add static map to.
   * @returns {Parcel[]} - Returns the list of parcels with static map added.
   */
  addStaticMapToParcels(parcels: Parcel[]): Parcel[] {
    const allParcels: Parcel[] = [];
    parcels.forEach((parcel) => {
      const parcelWithMap = this.decodeGeoJsonField(parcel);
      allParcels.push(parcelWithMap);
    });
    return allParcels;
  }

  /**
   * Decodes the GeoJSON field of a parcel.
   *
   * @param {any} field - The GeoJSON field of a parcel.
   * @returns {Parcel} - Returns the parcel with the decoded GeoJSON field.
   */
  private decodeGeoJsonField(field) {
    const geoJsonTmp: any = this.getGeoJsonFeatureCollection();
    geoJsonTmp.features.push(this.getGeoJsonFeature());
    geoJsonTmp.features[0]['geometry'] = field.polygon;
    field.area = geoJsonTmp;
    field.staticMapUrl = this.staticMapsProxyService.getStaticMapUrl(
      field.area.features,
      undefined,
      100
    );
    return field;
  }

  /**
   * Gets the GeoJSON feature collection.
   *
   * @returns {any} - Returns the GeoJSON feature collection.
   */
  private getGeoJsonFeatureCollection() {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  /**
   * Gets the GeoJSON feature.
   *
   * @returns {any} - Returns the GeoJSON feature.
   */
  private getGeoJsonFeature() {
    return {
      type: 'Feature',
      properties: {},
      geometry: null,
    };
  }
}
